import { createSlice } from "@reduxjs/toolkit";
import { getProtectedFile } from "../utils/StorageManager";
import { attachDownloadFinalStateSync } from "./downloadFinalStateSync";
import { selectDownloadDirectory } from "./downloadFilesystem";
import { getDownloadManager } from "./downloadManagerSingleton";

export const DOWNLOAD_SLICE_KEY = "downloadTransfers";

const TERMINAL_STATUSES = new Set(["complete", "cancelled"]);

const initialState = {
  jobs: {},
  activeJobIds: [],
  expandedJobIds: [],
  hydrated: false,
};

function isActiveDownload(job) {
  return Boolean(job && job.id && !TERMINAL_STATUSES.has(job.status));
}

function addActiveJobId(state, jobId) {
  if (jobId && !state.activeJobIds.includes(jobId)) {
    state.activeJobIds.push(jobId);
  }
}

function removeActiveJobId(state, jobId) {
  state.activeJobIds = state.activeJobIds.filter((id) => id !== jobId);
}

function addExpandedJobId(state, jobId) {
  if (jobId && !state.expandedJobIds.includes(jobId)) {
    state.expandedJobIds.push(jobId);
  }
}

function removeExpandedJobId(state, jobId) {
  state.expandedJobIds = state.expandedJobIds.filter((id) => id !== jobId);
}

function upsertDownloadJob(state, job) {
  if (!job || !job.id) {
    return;
  }

  state.jobs[job.id] = job;

  if (isActiveDownload(job)) {
    addActiveJobId(state, job.id);
  } else {
    removeActiveJobId(state, job.id);
    removeExpandedJobId(state, job.id);
  }
}

function normalizeHydratedJobs(jobs = []) {
  return jobs.filter((job) => job && job.id);
}

const downloadSlice = createSlice({
  name: "downloadTransfers",
  initialState,
  reducers: {
    hydrateDownloadsSuccess(state, { payload }) {
      const jobs = normalizeHydratedJobs(payload);

      state.jobs = jobs.reduce((acc, job) => {
        acc[job.id] = job;
        return acc;
      }, {});
      state.activeJobIds = jobs
        .filter(isActiveDownload)
        .map((job) => job.id);
      state.expandedJobIds = [];
      state.hydrated = true;
    },
    registerDownload(state, { payload }) {
      upsertDownloadJob(state, payload);
      if (isActiveDownload(payload)) {
        addExpandedJobId(state, payload.id);
      }
    },
    updateDownload(state, { payload }) {
      upsertDownloadJob(state, payload);
    },
    expandDownload(state, { payload }) {
      const jobId = typeof payload === "string" ? payload : payload && payload.jobId;

      if (jobId) {
        addExpandedJobId(state, jobId);
      }
    },
    removeDownload(state, { payload }) {
      const jobId = typeof payload === "string" ? payload : payload && payload.jobId;

      if (!jobId) {
        return;
      }

      delete state.jobs[jobId];
      removeActiveJobId(state, jobId);
      removeExpandedJobId(state, jobId);
    },
  },
});

export const {
  hydrateDownloadsSuccess,
  expandDownload,
  registerDownload,
  updateDownload,
  removeDownload,
} = downloadSlice.actions;

function getInjectedDownloadManager(manager, thunkExtraArgument) {
  const injectedManager =
    manager ||
    (thunkExtraArgument && thunkExtraArgument.downloadManager) ||
    (thunkExtraArgument && thunkExtraArgument.manager);

  if (
    !injectedManager ||
    typeof injectedManager.listIncompleteDownloads !== "function"
  ) {
    throw new Error(
      "A download manager with listIncompleteDownloads is required."
    );
  }

  return injectedManager;
}

export const hydrateDownloads = ({ manager } = {}) => async (
  dispatch,
  getState,
  thunkExtraArgument
) => {
  const downloadManager = getInjectedDownloadManager(
    manager,
    thunkExtraArgument
  );
  const jobs = (await downloadManager.listIncompleteDownloads()) || [];

  dispatch(hydrateDownloadsSuccess(jobs));

  return jobs;
};

function selectDownloadState(state) {
  return state[DOWNLOAD_SLICE_KEY] || initialState;
}

export const selectDownloads = (state) => selectDownloadState(state).jobs || {};

export const selectDownloadById = (state, jobId) =>
  selectDownloads(state)[jobId] || null;

export const selectActiveDownloads = (state) => {
  const downloadState = selectDownloadState(state);
  const jobs = downloadState.jobs || {};

  return (downloadState.activeJobIds || [])
    .map((jobId) => jobs[jobId])
    .filter(isActiveDownload);
};

export const selectDownloadHydrated = (state) =>
  Boolean(selectDownloadState(state).hydrated);

export const selectExpandedDownloadIds = (state) =>
  selectDownloadState(state).expandedJobIds || [];

export const pauseDownloadJob = ({ jobId, manager } = {}) => async (
  dispatch,
  getState
) => {
  if (!jobId) {
    throw new Error("A download job id is required.");
  }

  const downloadManager = manager || (await getDownloadManager());

  if (
    !downloadManager ||
    typeof downloadManager.pauseDownload !== "function"
  ) {
    throw new Error("A download manager with pauseDownload is required.");
  }

  const pauseResult = await Promise.resolve(
    downloadManager.pauseDownload({ jobId })
  );

  if (pauseResult) {
    const existingJob = selectDownloadById(getState(), jobId);

    if (existingJob && existingJob.status === "running") {
      dispatch(
        updateDownload({
          ...existingJob,
          status: "paused",
          updatedAt: new Date().toISOString(),
        })
      );
    }
  }

  return pauseResult;
};

export const resumeDownloadJob = ({
  jobId,
  manager,
  rootDirectoryHandle,
  getSignedUrl,
} = {}) => async (dispatch, getState) => {
  if (!jobId) {
    throw new Error("A download job id is required.");
  }

  const existingJob = selectDownloadById(getState(), jobId);

  if (existingJob && existingJob.status === "cancelled") {
    return null;
  }

  const downloadManager = manager || (await getDownloadManager());

  if (
    !downloadManager ||
    typeof downloadManager.resumeDownload !== "function"
  ) {
    throw new Error("A download manager with resumeDownload is required.");
  }

  const selectedRootDirectoryHandle =
    rootDirectoryHandle || (await selectDownloadDirectory());
  const signedUrlProvider = getSignedUrl || getProtectedFile;
  const resumeResult = await Promise.resolve(
    downloadManager.resumeDownload({
      jobId,
      rootDirectoryHandle: selectedRootDirectoryHandle,
      getSignedUrl: signedUrlProvider,
    })
  );

  if (resumeResult) {
    attachDownloadFinalStateSync({
      jobId,
      manager: downloadManager,
      dispatch,
      sessionRecord: resumeResult,
      updateDownloadAction: updateDownload,
    });

    const resumedJob = selectDownloadById(getState(), jobId);

    if (
      resumedJob &&
      ["paused", "failed"].includes(resumedJob.status)
    ) {
      dispatch(
        updateDownload({
          ...resumedJob,
          status: "running",
          updatedAt: new Date().toISOString(),
        })
      );
    }

    dispatch(expandDownload({ jobId }));
  }

  return resumeResult;
};

export const cancelDownloadJob = ({ jobId, manager } = {}) => async (
  dispatch,
  getState
) => {
  if (!jobId) {
    throw new Error("A download job id is required.");
  }

  const downloadManager = manager || (await getDownloadManager());

  if (
    !downloadManager ||
    typeof downloadManager.cancelDownload !== "function"
  ) {
    throw new Error("A download manager with cancelDownload is required.");
  }

  const cancelledJob = await Promise.resolve(
    downloadManager.cancelDownload({ jobId })
  );

  if (cancelledJob) {
    dispatch(updateDownload(cancelledJob));
  }

  return cancelledJob;
};

export default downloadSlice.reducer;
